import { FormProvider, UseFormReturn, get, useFormState } from 'react-hook-form';
import { CheckboxField } from '@brighte/form-components';
import { E2515_CASH_APPLICATION_ANNUAL_INCOME_THRESHOLD, HouseholdType, Period } from '@brighte/validation';
import { ConfirmFileUpload } from '@components/FileUpload';
import { RepaymentInput } from '@components/RepaymentInput';
import { useApplicationData } from '@provider';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Field } from '@spark-web/field';
import { InformationCircleIcon } from '@spark-web/icon';
import { useDisclosure } from '@spark-web/utils';
import { HOUSEHOLD, INCOME_VERIFICATION, productIds } from '@utils';
import IncomeVerificationOptions from '../IncomeVerification/IncomeVerificationOptions';

type Props = Readonly<UseFormReturn<HouseholdType>>;

export default function HouseholdIncome(formMethods: Props) {
  const { isOpen, onOpen, onToggle } = useDisclosure();
  const { control, register, watch } = formMethods;
  const { quote, Finance: finance } = useApplicationData(['quote', 'Finance']);
  const { errors } = useFormState({ control });

  const [householdIncomeAmount, primaryIncomeDocuments, isPrimaryVerificationSkipped, isBankStatementComplete] = watch([
    'applicant.income.householdIncome.amount',
    'applicant.income.primary.documents',
    'applicant.income.isSkipPrimaryIncome',
    'applicant.income.primary.bankStatementComplete',
  ]);

  const { INCOME_WARNING, BANK_STATEMENT, DOCUMENTS_PROVIDED, SKIP_PRIMARY_INCOME, SKIP_PRIMARY_INCOME_DESCRIPTION } =
    INCOME_VERIFICATION;

  const financialProductId = quote?.financialProduct?.id || '';
  const vendorId = quote?.vendor?.publicId || '';
  const isElectrify2515 = financialProductId === productIds.ELECTRIFY_2515;
  const isCashApplication = finance?.payment?.isCashApplication ?? false;

  // For E2515 cash applications, use household income amount to determine if verification is required
  const isPrimaryIncomeVerificationRequired = () => {
    return (householdIncomeAmount || 0) <= E2515_CASH_APPLICATION_ANNUAL_INCOME_THRESHOLD;
  };

  const documentUploaded = primaryIncomeDocuments ?? [];
  const isPrimaryIncomeVerified = Boolean(documentUploaded.length > 0 || isBankStatementComplete);

  const isHouseholdDocumentError = !!get(errors, 'applicant.income.primary.documents');

  const renderVerificationDetail = () => {
    // Only show verification for Electrify 2515 cash applications
    if (!isElectrify2515 || !isCashApplication) return null;

    // If primary income verification is not required (household income > threshold), don't show verification
    if (!isPrimaryIncomeVerificationRequired()) {
      return null;
    }

    // If user has skipped verification via checkbox, don't show verification details
    if (isPrimaryVerificationSkipped) {
      return null;
    }

    return (
      <>
        <Alert tone="caution" icon={InformationCircleIcon}>
          {INCOME_WARNING}
        </Alert>
        <Button prominence="low" onClick={onOpen}>
          {isPrimaryIncomeVerified ? 'Provide primary income' : 'Verify primary income'}
        </Button>
        {isPrimaryIncomeVerified && !isOpen && (
          <ConfirmFileUpload confirmationText={isBankStatementComplete ? BANK_STATEMENT : DOCUMENTS_PROVIDED} />
        )}
        {!isPrimaryIncomeVerified && isHouseholdDocumentError && (
          <Alert tone="critical" icon={InformationCircleIcon}>
            Income verification documents are required
          </Alert>
        )}
      </>
    );
  };

  return (
    <>
      <Field label={HOUSEHOLD.HOUSEHOLD_INCOME} description={HOUSEHOLD.HOUSEHOLD_INCOME_DESCRIPTION}>
        <RepaymentInput
          period="yearly"
          name="applicant.income.householdIncome.amount"
          control={control}
          label=""
          data-testid="household-income-amount"
        />
        <input type="hidden" {...register('applicant.income.householdIncome.period')} value={Period.Annually} />
      </Field>

      {isElectrify2515 && isCashApplication && isPrimaryIncomeVerificationRequired() && (
        <>
          {renderVerificationDetail()}
          {!isPrimaryVerificationSkipped && (
            <FormProvider {...formMethods}>
              <IncomeVerificationOptions
                name="applicant.income.primary"
                isOpen={isOpen}
                onToggle={onToggle}
                financialProductId={financialProductId}
                vendorId={vendorId}
              />
            </FormProvider>
          )}
          {!isPrimaryIncomeVerified && (
            <Box border="neutral" padding="large" borderRadius="small">
              <CheckboxField
                name="applicant.income.isSkipPrimaryIncome"
                control={control}
                label={SKIP_PRIMARY_INCOME}
              />
            </Box>
          )}
          {isPrimaryVerificationSkipped && <Alert tone="info">{SKIP_PRIMARY_INCOME_DESCRIPTION}</Alert>}
        </>
      )}

      {isElectrify2515 && isCashApplication && isPrimaryIncomeVerificationRequired() && (
        <>
          <input type="hidden" {...register('applicant.income.primary.bankStatementToken')} />
          <input hidden type="checkbox" {...register('applicant.income.primary.bankStatementComplete')} />
          <input hidden type="checkbox" {...register('applicant.income.isSkipPrimaryIncome')} />
        </>
      )}
    </>
  );
}
